import React, { useEffect, useState } from 'react';

interface SwitchWorkspaceReasonModalProps {
  isOpen: boolean;
  workspaceName: string;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * Reason capture for switching into another organization's workspace.
 *
 * Switching to a different tenant is a super-admin impersonation, which the backend
 * only permits with an X-Impersonation-Reason that is written to the audit log. This
 * modal collects that reason and matches the CreateTenantModal styling so the Workspace
 * Switcher stays visually consistent.
 */
export const SwitchWorkspaceReasonModal: React.FC<SwitchWorkspaceReasonModalProps> = ({
  isOpen,
  workspaceName,
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset local state each time the modal is opened for a new target.
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = (e?: React.FormEvent) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const trimmed = reason.trim();
    if (!trimmed) {
      setError('A reason is required to enter another workspace.');
      return;
    }
    onConfirm(trimmed);
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        backgroundColor: '#1f2937',
        color: '#f9fafb',
        padding: '24px',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '480px',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Enter Workspace</h3>
          <button
            onClick={onCancel}
            style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '1.2rem' }}
            aria-label="Cancel"
          >
            ✕
          </button>
        </div>

        <form id="switch-workspace-reason-form" onSubmit={handleConfirm}>
          <p style={{ margin: '0 0 16px', fontSize: '0.875rem', color: '#d1d5db', lineHeight: 1.5 }}>
            You are about to enter <strong style={{ color: '#fff' }}>{workspaceName}</strong>. This access is
            recorded in the audit log. Please note why you need to enter this workspace.
          </p>

          {error && (
            <div style={{ backgroundColor: '#7f1d1d', color: '#fca5a5', padding: '8px 12px', borderRadius: '4px', marginBottom: '12px', fontSize: '0.875rem' }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label
              htmlFor="switch-workspace-reason"
              style={{ display: 'block', fontSize: '0.875rem', marginBottom: '4px', color: '#d1d5db' }}
            >
              Reason *
            </label>
            <input
              id="switch-workspace-reason"
              type="text"
              value={reason}
              onChange={e => { setReason(e.target.value); if (error) setError(null); }}
              placeholder="e.g. Investigating a support issue"
              autoFocus
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#374151',
                border: '1px solid #4b5563',
                borderRadius: '4px',
                color: '#fff',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4b5563',
                border: 'none',
                borderRadius: '4px',
                color: '#fff',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                backgroundColor: '#2563eb',
                border: 'none',
                borderRadius: '4px',
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Enter Workspace
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
